import { AfterViewInit, Component, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';

export interface INumberCellEditorParams extends ICellEditorParams {
  eventKey: string;
  /** Min allowed value. */
  min?: number;
  /** Max allowed value. */
  max?: number;
  /** Number of digits allowed after the decimal point. */
  precision?: number;
  /**
   * Size of the value change when stepping up/down, starting from `min` or the initial value if provided.
   * Step is also the difference between valid values.
   * If the user-provided value isn't a multiple of the step value from the starting value, it will be considered invalid.
   * Defaults to any value allowed.
   */
  step?: number;
  /**
   * Display stepper buttons in editor. Note: Does not work when `preventStepping` is `true`.
   * @default false
   */
  showStepperButtons?: boolean;
  /**
   * Set to `true` to allow negative numeric input.
   * @default false
   */
  allowNegInd?: boolean;
  /** Restrict input to whole numbers only. */
  integerOnly?: boolean;
}

@Component({
  selector: 'xpo-numeric-cell-editor',
  templateUrl: './numeric-editor.component.html',
  styleUrls: ['./numeric-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'xpo-AgGrid-cellEditor--numeric',
  },
})
export class NumericEditorComponent implements ICellEditorAngularComp, AfterViewInit {
  @ViewChild('input', { read: ViewContainerRef }) input!: ViewContainerRef;
  private params: INumberCellEditorParams;
  private isStopping = false;
  value;
  min: number;
  max: number;
  step: number;
  allowNegInd: boolean;
  integerOnly: boolean;
  private focusAfterAttached: boolean;

  agInit(params: INumberCellEditorParams): void {
    this.params = params;
    this.allowNegInd = params.allowNegInd ?? false;
    this.integerOnly = params.integerOnly ?? false;
    this.focusAfterAttached = params.cellStartedEdit;
    const key = params.eventKey;
    const isNegativeSign = this.allowNegInd && key === '-';
    const isNumeric = this.isCharNumeric(key);

    this.value = isNumeric || isNegativeSign ? key : params.value;
    this.min = params.min;
    this.max = params.max;
    this.step = params.step;
  }

  getValue(): number | null {
    const value = this.value,
      parsed = parseFloat(value);
    return value === '' || value == null ? null : isNaN(parsed) ? null : parsed;
  }

  onKeyDown(event: any): void {
    if (!event.key || event.key.length !== 1 || this.isNumericKey(event)) {
      return;
    }
    this.input.element.nativeElement.focus();

    if (event.preventDefault) {
      event.preventDefault();
    }
  }

  ngAfterViewInit(): void {
    window.setTimeout(() => {
      if (this.focusAfterAttached) {
        this.input.element.nativeElement.focus();
        this.input.element.nativeElement.select();
      }
    });
  }

  // when we tab into this editor, we want to focus the contents
  focusIn() {
    this.input.element.nativeElement.focus();
    this.input.element.nativeElement.select();
  }

  // when we tab out of the editor, this gets called
  focusOut() {
    this.stopEditing();
  }

  onBlur(): void {
    this.stopEditing();
  }

  private stopEditing(): void {
    if (this.isStopping) {
      return;
    }

    this.isStopping = true;
    window.setTimeout(() => {
      this.params?.stopEditing?.();
      this.isStopping = false;
    });
  }

  private isCharNumeric(charStr: string | null): boolean {
    if (charStr == null) {
      return false;
    }

    return this.integerOnly ? !!/\d/.test(charStr) : !!/[\d.]/.test(charStr);
  }

  private isCharNegNumeric(charStr: string | null): boolean {
    if (charStr === null) {
      return false;
    }

    if ((this.integerOnly ? /\d/ : /[\d.]/).test(charStr)) {
      return true;
    }

    if (charStr === '-') {
      const inputElement = this.input.element.nativeElement;
      const value = inputElement.value || '';
      const start = inputElement.selectionStart;
      const end = inputElement.selectionEnd;

      // Simulate the new value after this keypress
      const newValue = value.substring(0, start) + '-' + value.substring(end);

      // Validate: Must only contain one '-', and it must be at the start
      return newValue.indexOf('-') === 0 && newValue.lastIndexOf('-') === 0;
    }

    return false;
  }

  private isNumericKey(event: any): boolean {
    const charStr = event.key;
    return this.allowNegInd ? this.isCharNegNumeric(charStr) : this.isCharNumeric(charStr);
  }
}
